import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace, NAV_GROUP_PATHS } from '../../hooks/useWorkspace';
import SidebarLayout from './SidebarLayout';
import {
  LayoutDashboard, CalendarDays, FileText, ClipboardList, Users, UserCog,
  Package, Crown, Award, CalendarCheck, Megaphone, ShieldCheck, Shield,
  Clock, Merge, BookOpen, BarChart3, Settings as SettingsIcon, Gem, Wallet,
  Truck, PhoneCall, UserCheck, Bell, Wrench, AlertTriangle, MessageSquare,
  Database, ScrollText, Boxes, Building2, Layers, ArrowLeftRight, Archive,
  Building, Briefcase, TrendingUp, Cog, GitBranch, GitPullRequest, Trash2,
  ClipboardCheck, Settings2, Star, DollarSign, Users2, Link as LinkIcon, AlertOctagon,
} from 'lucide-react';
import { WORKFORCE_TABS } from '../../hubs/workforceTabs';
import { INVENTORY_TABS } from '../../hubs/inventoryTabs';
import { PM_TABS } from '../../hubs/pmTabs';
import { SETTINGS_TABS } from '../../pages/admin/AdminSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_TIER = ['super_admin', 'admin'];
const MANAGER_TIER = ['service_manager', 'construction_manager', 'office_staff', 'manager'];
const ALL_INTERNAL = [...ADMIN_TIER, ...MANAGER_TIER];

const can = (role, allow) => allow.includes(role);

const AdminLayout = () => {
  const { user, hasPermission } = useAuth();
  const workspace = useWorkspace();
  const role = user?.role;
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingEdits, setPendingEdits] = useState(0);
  const [pendingJobApprovals, setPendingJobApprovals] = useState(0);
  // iter195 — Follow-up queue badge (open = new + contacted + scheduled).
  const [followupOpen, setFollowupOpen] = useState(0);
  // iter196 — Field Notes Review queue badge (unreviewed count).
  const [fieldNotesUnreviewed, setFieldNotesUnreviewed] = useState(0);
  // iter205 — Office Billing Review queue badge (pending office-review drafts).
  const [officeReviewPending, setOfficeReviewPending] = useState(0);
  const REVIEWER_TIER = ['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager'];

  useEffect(() => {
    let alive = true;
    const fetchPending = async () => {
      if (!ADMIN_TIER.includes(role)) return;
      try {
        const { data } = await axios.get(`${API}/admin/users/pending`, { withCredentials: true });
        if (alive) setPendingCount((data || []).length);
      } catch { /* ignore */ }
    };
    const fetchEdits = async () => {
      if (!REVIEWER_TIER.includes(role)) return;
      try {
        const { data } = await axios.get(`${API}/admin/edits/stats`, { withCredentials: true });
        if (alive) setPendingEdits(data?.pending || 0);
      } catch { /* ignore */ }
    };
    const fetchJobApprovals = async () => {
      if (!REVIEWER_TIER.includes(role)) return;
      try {
        const { data } = await axios.get(`${API}/admin/job-completions/counts`, { withCredentials: true });
        if (alive) setPendingJobApprovals(data?.pending_approval || 0);
      } catch { /* ignore */ }
    };
    const fetchFollowups = async () => {
      // iter195 — Any internal user can hit /followups.
      try {
        const { data } = await axios.get(`${API}/admin/followups/counts`, { withCredentials: true });
        if (alive) setFollowupOpen(data?.open || 0);
      } catch { /* ignore */ }
    };
    const fetchFieldNotes = async () => {
      // iter196 — Field Notes Review sidebar badge.
      try {
        const { data } = await axios.get(`${API}/admin/field-notes/counts`, { withCredentials: true });
        if (alive) setFieldNotesUnreviewed(data?.unreviewed || 0);
      } catch { /* ignore */ }
    };
    const fetchOfficeReview = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/office-billing-review?status=office_review_required`, { withCredentials: true });
        if (alive) setOfficeReviewPending(data?.counts?.office_review_required || 0);
      } catch { /* ignore */ }
    };
    fetchPending();
    fetchEdits();
    fetchJobApprovals();
    fetchFollowups();
    fetchFieldNotes();
    fetchOfficeReview();
    const id = setInterval(() => { fetchPending(); fetchEdits(); fetchJobApprovals(); fetchFollowups(); fetchFieldNotes(); fetchOfficeReview(); }, 60000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line
  }, [role]);

  const operations = {
    label: 'Operations',
    hubIcon: LayoutDashboard,
    items: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, allow: ALL_INTERNAL },
      { path: '/admin/schedule', label: 'Schedule', icon: CalendarDays, allow: ALL_INTERNAL },
      { path: '/admin/service-requests', label: 'Service Requests', icon: Wrench, allow: ALL_INTERNAL },
      { path: '/admin/followups', label: 'Follow-Ups', icon: MessageSquare, allow: ALL_INTERNAL, badge: followupOpen > 0 ? String(followupOpen) : null },
      { path: '/admin/field-notes', label: 'Field Notes', icon: ClipboardList, allow: ALL_INTERNAL, badge: fieldNotesUnreviewed > 0 ? String(fieldNotesUnreviewed) : null },
      { path: '/admin/customers', label: 'Customers', icon: Users, allow: ALL_INTERNAL },
      { path: '/admin/estimates', label: 'Estimates', icon: ClipboardList, allow: ALL_INTERNAL },
    ],
  };

  const billing = {
    label: 'Billing',
    hubIcon: Wallet,
    items: [
      { path: '/admin/invoices', label: 'Invoices', icon: FileText, allow: ALL_INTERNAL },
      { path: '/admin/billing-missing', label: 'Billing Missing', icon: AlertOctagon, allow: ALL_INTERNAL, badge: officeReviewPending > 0 ? String(officeReviewPending) : null },
      { path: '/admin/payment-diagnostic', label: 'Payment Diagnostic', icon: FileText, allow: ADMIN_TIER },
      { path: '/admin/reports/revenue-pipeline', label: 'Revenue Pipeline', icon: DollarSign, allow: REVIEWER_TIER, perms: ['view_financial'] },
    ],
  };

  const workforce = {
    label: 'Workforce',
    hubIcon: Users2,
    items: [
      // iter75 — Workforce Hub: daily workforce tools live as tabs inside the hub
      // (Employees, Timesheets, Time Off, Bonuses, Payroll). Old paths redirect.
      { path: '/admin/workforce', label: 'Workforce Hub', icon: Users2, allow: ALL_INTERNAL,
        badges: { '/admin/workforce/job-approvals': pendingJobApprovals > 0 ? String(pendingJobApprovals) : null,
                  '/admin/workforce/approvals': pendingCount > 0 ? String(pendingCount) : null } },
      // iter254 — Admin Workforce: the workforce configuration/management group.
      { label: 'Admin Workforce', icon: UserCog, children: [
        { path: '/admin/users', label: 'User Management', icon: UserCog, allow: ADMIN_TIER, perms: ['manage_employees', 'invite_employees', 'onboard_employees'], badge: pendingCount > 0 ? String(pendingCount) : null },
        { path: '/admin/roles', label: 'Role Manager', icon: ShieldCheck, allow: ADMIN_TIER },
        { path: '/admin/permissions', label: 'Roles & Permissions', icon: ShieldCheck, allow: ADMIN_TIER },
        { path: '/admin/reports', label: 'Reports', icon: BarChart3, allow: ADMIN_TIER, perms: ['view_financial'] },
        { path: '/admin/reports/tech-performance', label: 'Technician Performance', icon: Award, allow: REVIEWER_TIER, perms: ['view_financial'] },
        { path: '/admin/on-call', label: 'On-Call', icon: PhoneCall, allow: [...ADMIN_TIER, 'service_manager', 'construction_manager'] },
        { path: '/admin/workforce/holidays', label: 'Holidays & Benefits', icon: CalendarCheck, allow: [...ADMIN_TIER, 'service_manager', 'construction_manager', 'manager', 'office_staff'], perms: ['manage_employees', 'edit_pto_balance'] },
      ] },
    ],
  };

  const inventoryKB = {
    label: 'Inventory & Knowledge',
    hubIcon: Boxes,
    items: [
      { path: '/admin/inventory-hub', label: 'Inventory Hub', icon: Boxes, allow: ALL_INTERNAL },
      { path: '/admin/service-catalog', label: 'Service Catalog', icon: BookOpen, allow: ALL_INTERNAL },
      { path: '/admin/pricebook-import', label: 'Pricebook Import', icon: FileText, allow: ADMIN_TIER },
      // Vehicles keeps its Workforce-Hub route; surfaced here for discoverability.
      { path: '/admin/workforce/vehicles', label: 'Vehicles', icon: Truck, allow: [...ADMIN_TIER, 'service_manager', 'construction_manager'] },
      // Knowledge Base pulled out of the Inventory Hub tabs into the left nav (same route).
      { path: '/admin/inventory-hub/knowledge-base', label: 'Knowledge Base', icon: BookOpen, allow: ALL_INTERNAL },
    ],
  };

  const jobSetup = {
    label: 'Job & Workflow Setup',
    hubIcon: Briefcase,
    items: [
      { path: '/admin/departments', label: 'Departments / Trades', icon: Building2, allow: [...ADMIN_TIER, ...MANAGER_TIER] },
      { path: '/admin/divisions', label: 'Divisions', icon: Layers, allow: ADMIN_TIER },
      { path: '/admin/service-types', label: 'Service Types', icon: Briefcase, allow: [...ADMIN_TIER, ...MANAGER_TIER] },
      { path: '/admin/job-templates', label: 'Job Templates', icon: ClipboardList, allow: [...ADMIN_TIER, ...MANAGER_TIER] },
      { path: '/admin/form-builder', label: 'Completion Form Builder', icon: Settings2, allow: ADMIN_TIER },
    ],
  };

  const sales = {
    label: 'Sales & Opportunities',
    hubIcon: TrendingUp,
    items: [
      { path: '/admin/opportunities', label: 'Opportunities', icon: TrendingUp, allow: ALL_INTERNAL },
      { path: '/admin/reports/opportunity-board', label: 'Opportunity Board', icon: Star, allow: REVIEWER_TIER },
    ],
  };

  const membersVip = {
    label: 'VIP Program',
    hubIcon: Gem,
    items: [
      { path: '/admin/vips', label: 'VIP Members', icon: Gem, allow: [...ADMIN_TIER, 'office_staff'] },
      { path: '/admin/vip-settings', label: 'VIP Settings', icon: Award, allow: ADMIN_TIER },
      { path: '/admin/vip-terms', label: 'VIP Terms', icon: ScrollText, allow: ADMIN_TIER },
    ],
  };

  const propertyMgmt = {
    label: 'Property Management',
    hubIcon: Building,
    items: [
      { path: '/admin/pm', label: 'Property Management Hub', icon: Building, allow: ALL_INTERNAL },
    ],
  };

  const marketingComms = {
    label: 'Marketing & Communications',
    hubIcon: Megaphone,
    items: [
      { path: '/admin/communication', label: 'Communication Settings', icon: MessageSquare, allow: ALL_INTERNAL },
      { path: '/admin/reports/marketing', label: 'Marketing', icon: Megaphone, allow: REVIEWER_TIER, perms: ['view_financial'] },
      { path: '/admin/promos', label: 'Promo Links & Campaigns', icon: LinkIcon, allow: ADMIN_TIER },
      { path: '/admin/announcements', label: 'Announcements', icon: Megaphone, allow: [...ADMIN_TIER, 'service_manager', 'construction_manager', 'office_staff'] },
      { path: '/admin/sms', label: 'SMS / Twilio', icon: MessageSquare, allow: ADMIN_TIER, perms: ['company_settings'] },
    ],
  };

  const system = {
    label: 'System & Administration',
    hubIcon: Cog,
    items: [
      { path: '/admin/settings', label: 'System Settings', icon: SettingsIcon, allow: ADMIN_TIER },
      { path: '/admin/audit-log', label: 'Audit Log', icon: Shield, allow: ADMIN_TIER, perms: ['view_audit_log'] },
      { path: '/admin/change-approvals', label: 'Change Approvals', icon: GitBranch, allow: [...ADMIN_TIER, 'manager', 'service_manager', 'construction_manager'], badge: pendingEdits > 0 ? String(pendingEdits) : null },
      { path: '/admin/my-changes', label: 'My Changes', icon: GitPullRequest, allow: ALL_INTERNAL },
      { path: '/admin/data-tools', label: 'Data Tools', icon: Database, allow: ADMIN_TIER },
      { path: '/admin/trash', label: 'Trash', icon: Trash2, allow: ['super_admin'] },
      // iter254 — Danger Zone relocated behind an Advanced / Security subgroup
      // (navigation only; re-auth hardening is a separate follow-up task).
      { label: 'Advanced / Security', icon: AlertTriangle, children: [
        { path: '/admin/system', label: 'Danger Zone', icon: AlertTriangle, allow: ['super_admin'] },
      ] },
    ],
  };

  const itemAllowed = (it) => {
    if (can(role, it.allow)) return true;
    const permList = it.perms || (it.perm ? [it.perm] : []);
    return permList.some((p) => hasPermission(p));
  };

  // iter160/iter254 — an item is visible if the role matches OR the user holds any
  // optional `perm`/`perms`. Parent items with `children` are visible when at least
  // one child survives the filter (empty parents are dropped).
  const filterSection = (sec) => ({
    ...sec,
    items: sec.items
      .map((it) => {
        if (it.children) {
          const children = it.children.filter(itemAllowed);
          return children.length ? { ...it, children } : null;
        }
        return itemAllowed(it) ? it : null;
      })
      .filter(Boolean),
  });

  const permitted = [operations, billing, workforce, inventoryKB, jobSetup, sales, membersVip, propertyMgmt, marketingComms, system]
    .map(filterSection)
    .filter((sec) => sec.items.length > 0);
  // iter318 — role Menu Groups organize (never grant) navigation: configured groups become the
  // everyday section, every other PERMITTED item moves under "More". No Menu Groups or
  // Super Admin/Admin → full navigation as before.
  const navGroups = workspace?.nav_groups || [];
  const sections = !navGroups.length ? permitted : (() => {
    const prefixes = navGroups.flatMap((g) => NAV_GROUP_PATHS[g] || []);
    const isPrimary = (it) => it.path && prefixes.some((p) => it.path === p || it.path.startsWith(`${p}/`));
    const flat = permitted.flatMap((sec) => sec.items);
    const primary = flat.filter(isPrimary).sort((a, b) => prefixes.findIndex((p) => a.path.startsWith(p)) - prefixes.findIndex((p) => b.path.startsWith(p)));
    const more = flat.filter((it) => !isPrimary(it));
    return [
      { label: workspace.role_name || 'Workspace', hubIcon: LayoutDashboard, items: primary, primary: true },
      ...(more.length ? [{ label: 'More', hubIcon: Layers, items: more, defaultCollapsed: true }] : []),
    ];
  })();

  // iter75 — Register hub-tab paths so SidebarLayout's Favorites section can render them with
  // a label + icon. Each tab's `allow` is a Set; we filter against the current role so users
  // never see a pinned favorite they don't have permission for.
  const hubTabs = [
    ...WORKFORCE_TABS.filter((t) => t.allow.has(role)).map((t) => ({ path: t.path, label: `Workforce · ${t.label}`, icon: t.icon, section: 'Workforce' })),
    ...INVENTORY_TABS.filter((t) => t.allow.has(role) && !t.hidden).map((t) => ({ path: t.path, label: `Inventory · ${t.label}`, icon: t.icon, section: 'Inventory' })),
    ...PM_TABS.filter((t) => t.allow.has(role)).map((t) => ({ path: t.path, label: `PM · ${t.label}`, icon: t.icon, section: 'Property Management' })),
    // iter75 Phase 5 — System Settings tabs pinnable via ?tab=KEY. Admin-only access.
    ...(ADMIN_TIER.includes(role) ? SETTINGS_TABS.map((t) => ({
      path: t.key === 'company' ? '/admin/settings' : `/admin/settings?tab=${t.key}`,
      label: `Settings · ${t.label}`,
      icon: t.icon,
      section: 'System',
    })) : []),
  ];

  return (
    <SidebarLayout
      sections={sections}
      hubTabs={hubTabs}
      portalLabel="Admin Console"
      showDepartmentSwitcher
      testIdPrefix="admin"
    />
  );
};

export default AdminLayout;
